import { ReactNode, useEffect, useRef } from 'react';
import {
    PrequalsApi,
    PrequalApplication,
    SubmitPrequalResponseDto,
    LoanApplicationSubmitSource,
} from '@servicetitan/financing-api-client/src/prequals-api';
import {
    ApplicationDataStore,
    SubmittingUserStore,
    useGreenSkyIovation,
} from '@servicetitan/financing-application-shared';
import { FinancedAmountStore, SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useMutation } from '@tanstack/react-query';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { ServerError } from '@servicetitan/financing-utils-shared';
import { preparePrequalificationData } from '../preparePrequalificationData';

// Module-level fingerprint persistence to survive hook re-initializations
let persistedFingerprint: string | undefined;

export interface UsePrequalSubmissionOptions {
    /** The prequalification ID to submit */
    prequalId: string;
    /** Whether to automatically submit on mount (default: true) */
    autoSubmit?: boolean;
    /** Custom success callback - if provided, overrides default workflow stage transition */
    onSuccess?: (data: { data: SubmitPrequalResponseDto }) => void;
    /** Custom error callback */
    onError?: (error: ServerError) => void;
}

export interface UsePrequalSubmissionReturn {
    /** Submit the prequalification application manually */
    submitPrequal: () => void;
    /** Whether the prequalification is currently being submitted */
    isSubmitting: boolean;
    /** Whether the prequalification has been submitted successfully */
    isSuccess: boolean;
    /** Any error that occurred during submission */
    error: ServerError | null;
    /** GreenSky Iovation component (if applicable) */
    iovationComponent: ReactNode | null;
    /** Whether this is a GreenSky provider */
    isGreenSky: boolean;
    /** GreenSky fingerprint (if applicable) */
    fingerprint: string | undefined;
    /** Reset the submission state */
    reset: () => void;
}

export const usePrequalSubmission = (
    options: UsePrequalSubmissionOptions,
): UsePrequalSubmissionReturn => {
    const { prequalId, autoSubmit = true, onSuccess, onError } = options;

    const [prequalsApi] = useDependencies(PrequalsApi);
    const { applicationData } = ApplicationDataStore.useStore();
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { submittingUser } = SubmittingUserStore.useStore();
    const { hasMailingAddress } = useProviderConfig();

    // Track submission state to prevent double submission in strict mode
    const hasSubmittedRef = useRef(false);

    // Use GreenSky Iovation
    const isGreenSky = setupData?.provider?.toLowerCase() === 'greensky';
    const { fingerprint: currentFingerprint, iovationComponent } = useGreenSkyIovation();

    // Use persisted fingerprint if available, otherwise use current fingerprint
    const fingerprint = persistedFingerprint ?? currentFingerprint;

    // Update persisted fingerprint when a new one is captured
    if (isGreenSky && currentFingerprint && !persistedFingerprint) {
        persistedFingerprint = currentFingerprint;
    }

    const application = preparePrequalificationData({
        applicationData,
        prequalId,
        tenantId: setupData!.tenantId,
        buId: setupData!.buId,
        merchantId: undefined,
        planNumbers: selectedPlan?.planNumber ? [selectedPlan.planNumber] : undefined,
        requestedAmount: financedAmount,
        technicianLastName: submittingUser?.lastName ?? undefined,
        technicianPhone: submittingUser?.phone ?? undefined,
        technicianEmail: submittingUser?.email ?? undefined,
        fingerPrint: isGreenSky ? fingerprint : undefined,
        source: applicationData.source as unknown as LoanApplicationSubmitSource,
        hasMailingAddress,
    });

    const mutation = useMutation<
        { data: SubmitPrequalResponseDto },
        ServerError,
        PrequalApplication
    >({
        mutationKey: ['submitPrequal', prequalId],
        mutationFn: (data: PrequalApplication) =>
            prequalsApi.prequalificationsSubmit(prequalId, data),
        onSuccess: (data: { data: SubmitPrequalResponseDto }) => {
            hasSubmittedRef.current = true;

            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error: ServerError) => {
            // Reset submission state on error to allow retry
            hasSubmittedRef.current = false;

            if (onError) {
                onError(error);
            }
        },
    });

    const submitPrequal = () => {
        if (!hasSubmittedRef.current) {
            // For GreenSky, wait for fingerprint before submitting
            if (isGreenSky && !fingerprint) {
                return;
            }
            mutation.mutate(application);
        }
    };

    const reset = () => {
        hasSubmittedRef.current = false;
        mutation.reset();
        // Reset persisted fingerprint for GreenSky to allow fresh capture on retry
        if (isGreenSky) {
            persistedFingerprint = undefined;
        }
    };

    // Auto-submit effect
    useEffect(() => {
        if (autoSubmit && !hasSubmittedRef.current) {
            // For GreenSky, wait for fingerprint before submitting
            if (isGreenSky && !fingerprint) {
                return;
            }
            submitPrequal();
        }

        return () => {
            // Reset submission state when component unmounts
            hasSubmittedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSubmit, isGreenSky, fingerprint]);

    return {
        submitPrequal,
        isSubmitting: mutation.isPending,
        isSuccess: mutation.isSuccess,
        error: mutation.error as ServerError | null,
        iovationComponent: isGreenSky ? iovationComponent : null,
        isGreenSky,
        fingerprint,
        reset,
    };
};
